import * as Popover from '@radix-ui/react-popover';
import { clsx } from 'clsx';
import {
  useMultipleSelection,
  useCombobox,
  UseComboboxPropGetters,
} from 'downshift';
import { useEffect, useState, useRef } from 'react';
import { Spinner } from '../spinner/spinner.tsx';
import {
  comboboxContainer,
  comboboxWrapper,
  comboboxInputContainer,
  comboboxInputWrapper,
  comboboxInput,
  comboboxMenu,
  spinner,
  comboboxLabel,
  comboboxIsFocused,
  inputError,
  comboboxError,
} from './multi-select-combobox.css.ts';
export { type UseComboboxPropGetters };

interface MultiSelectComboboxProps<T> {
  dropdownItems?: T[] | null;
  onInputChange: (inputValue: string) => void;
  selectedItems: T[];
  setSelectedItems: (items: T[]) => void;
  placeholder?: string;
  isLoading?: boolean;
  renderItemPill: (
    item: T,
    onRemove: (e: React.MouseEvent) => void,
  ) => React.ReactNode;
  isItemSelected: (item: T) => boolean;
  getItemId: (item: T) => string;
  label?: string;
  errorMessage?: string;
  isItemDisabled?: (item: T) => boolean;
  renderDropdownItems: ({
    getItemProps,
  }: {
    getItemProps: UseComboboxPropGetters<T>['getItemProps'];
  }) => React.ReactNode;
  wrapperClassName?: string;
  disabled?: boolean;
}

export function MultiSelectCombobox<T>({
  dropdownItems,
  onInputChange,
  selectedItems,
  setSelectedItems,
  placeholder,
  isLoading,
  getItemId,
  isItemSelected,
  renderItemPill,
  label,
  errorMessage,
  isItemDisabled = () => false,
  renderDropdownItems,
  wrapperClassName,
  disabled = false,
}: MultiSelectComboboxProps<T>) {
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    inputContainerRef.current?.scrollTo({
      top: inputContainerRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [selectedItems]);

  const { getSelectedItemProps, getDropdownProps, removeSelectedItem } =
    useMultipleSelection({
      selectedItems,
      onStateChange({ selectedItems: newSelectedItems, type }) {
        switch (type) {
          case useMultipleSelection.stateChangeTypes
            .SelectedItemKeyDownBackspace:
          case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
          case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
          case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
            setSelectedItems(newSelectedItems || []);
            break;
          default:
            break;
        }
      },
    });
  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getLabelProps,
    getItemProps,
    closeMenu,
  } = useCombobox({
    items: dropdownItems || [],
    isItemDisabled,
    selectedItem: null,
    inputValue,
    onInputValueChange: ({ inputValue, type }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputChange:
          onInputChange(inputValue);
          break;
        default:
          break;
      }
    },
    stateReducer(_, actionAndChanges) {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputClick:
        case useCombobox.stateChangeTypes.InputChange:
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true,
          };
        case useCombobox.stateChangeTypes.InputBlur:
          // Never close on blur. The modal Popover blocks interaction with
          // elements outside the dropdown, so blur only fires from overlay
          // taps or portal-related focus shifts. Closing is handled
          // explicitly via closeMenu() from Popover's onInteractOutside.
          return { ...changes, isOpen: true, selectedItem: null };
        default:
          return { ...changes };
      }
    },
    onStateChange({
      inputValue: newInputValue,
      type,
      selectedItem: newSelectedItem,
    }) {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputBlur:
          if (newSelectedItem) {
            if (isItemSelected(newSelectedItem)) {
              setSelectedItems(
                selectedItems.filter(
                  (item) => getItemId(item) !== getItemId(newSelectedItem),
                ),
              );
            } else {
              setSelectedItems([...selectedItems, newSelectedItem]);
            }
          }
          break;

        case useCombobox.stateChangeTypes.InputChange:
          setInputValue(newInputValue || '');
          break;
        default:
          break;
      }
    },
  });

  return (
    <div className={comboboxContainer}>
      {label && (
        <label className={comboboxLabel} {...getLabelProps()}>
          {label}
        </label>
      )}
      <Popover.Root modal open={isOpen && !disabled}>
        <Popover.Trigger asChild>
          <div
            className={clsx(
              comboboxWrapper,
              (isFocused || isOpen) && comboboxIsFocused,
              errorMessage && comboboxError,
              wrapperClassName,
            )}
            data-disabled={disabled ? '' : undefined}
            ref={inputContainerRef}
          >
            {isLoading && (
              <div className={spinner}>
                <Spinner />
              </div>
            )}
            <div className={comboboxInputContainer}>
              {selectedItems.map((selectedItemForRender) => (
                <span
                  key={`selected-item-${getItemId(selectedItemForRender)}`}
                  {...getSelectedItemProps({
                    selectedItem: selectedItemForRender,
                  })}
                >
                  {renderItemPill(selectedItemForRender, (e) => {
                    e.stopPropagation();
                    removeSelectedItem(selectedItemForRender);
                  })}
                </span>
              ))}
              <div className={comboboxInputWrapper}>
                <input
                  placeholder={placeholder}
                  className={comboboxInput}
                  {...getInputProps({
                    ...getDropdownProps({ preventKeyAction: false }),
                    disabled,
                    onFocus: () => setIsFocused(true),
                    onBlur: () => setIsFocused(false),
                  })}
                  autoComplete="off"
                />
              </div>
            </div>
          </div>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            sideOffset={1}
            onWheel={(e) => e.stopPropagation()}
            onInteractOutside={() => closeMenu()}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            asChild
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div
              className={comboboxMenu}
              {...getMenuProps({}, { suppressRefError: true })}
            >
              {renderDropdownItems({ getItemProps })}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      {errorMessage && <p className={inputError}>{errorMessage}</p>}
    </div>
  );
}
